/**
 * PaywallModal Component — Trial Redesign
 * 
 * Hard gate between onboarding (Steps 1-6) and discovery (Step 7).
 * Continuation tone: "Keep going with Cedar" header — not a sales pitch.
 * 
 * Behavior:
 * - When used as gate (showCloseButton=false): No dismiss, only path is starting trial
 * - When used from Settings: Close button visible for navigation back
 * 
 * Single CTA: "Start your free 7-day trial" → auto-selects Essentials monthly package
 * (com.coachcedar.app.basic.monthly). During the 7-day trial, Essentials users
 * get Chat+ level access (30 ad-hoc msgs/week). After trial, drops to touchpoint-only.
 * Trial auto-renews at the local Essentials monthly price after 7 days unless cancelled.
 * 
 * Sprint: Trial Redesign (Mission 33), updated Mission 35B (April 2026)
 * Date: February 2026, updated April 2026
 */

import React, { useState, useEffect, useRef } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { RAMP_FILL } from '@/utils/glassTokens';
import { makeStyles, onGlass, themed, useTheme, withAlpha } from '@/theme';
import { InstrumentSheen } from '@/components/native/InstrumentSheen.native';
import { CtaGradient } from '@/components/native/CtaGradient.native';
import { CanvasGradient } from '@/components/native/CanvasGradient.native';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Pressable,
  Platform,
  Linking,
  PanResponder,
  Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PurchasesPackage } from 'react-native-purchases';
import { subscriptionService, type SubscriptionTier } from '@/services/subscriptionService';
import { presentRevenueCatPaywall, shouldUseRevenueCatPaywall, type PaywallPlacement } from '@/services/revenueCatPaywall';
import { usePartnerOfferGate } from '@/hooks/usePartnerOfferGate';           // partner codes C5
import { PartnerOfferSheet } from '@/components/native/PartnerOfferSheet';     // partner codes C5
import { appAlert } from '@/services/appAlert';
import { resolveTrialDaysLeft } from '@/utils/trialDaysLeft';
import { getPlanFeatureBullets, getTierConfig, getProductIdForPlatform } from '@/config/subscriptionTiers';
import { Spacing } from '@/utils/styleConverter';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/services/storage';
import { secureTokenStorage } from '@/services/secureTokenStorage';
import { getApiBaseUrl } from '@/utils/apiConfig';
import { NativeIcon } from '@/components/native/NativeIcon';
import { AnimatedPressable } from '@/components/native/AnimatedPressable';
import { useRouter } from 'expo-router';               // 🌞 Summer50
import { useSubscription } from '@/hooks/useSubscription'; // 🌞 Summer50
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { trialFinePrint } from '@/utils/trialFinePrint';
import { freeMonthEndedStillApplies, packageStartsStoreTrial, paywallActiveTrialSubtitle, paywallExpiredContextCopy, paywallExpiredCopy, PAYWALL_EXPIRED_STATUS_UNKNOWN } from '@/utils/sevenFreeDaysCopy';
import { S50 } from '@/constants/summer50Copy';         // 🌞 Summer50
import { freeAccessPillLabel, freeAccessProgress } from '@/utils/freeAccessDuration';  // 🌞 Free Access

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PaywallMode = 'trial' | 'upgrade';

interface PaywallModalProps {
  visible: boolean;
  onClose: () => void;
  onPurchaseSuccess?: (tier: SubscriptionTier) => void;
  /** When false, hides close / dismiss affordance (hard-gate mode). Default true. */
  showCloseButton?: boolean;
  /** 'trial' = onboarding trial gate; 'upgrade' = choose Full or Extended from Settings. Default 'trial'. */
  mode?: PaywallMode;
  /** Optional userId when parent has it (e.g. drawer) so plans load before useAuth() has user. */
  userId?: string | null;
  /** Current subscription tier (trial | full | extended). In upgrade mode: trial = show 2 plans; full = upgrade only + cancel; extended = downgrade + cancel. Default 'trial'. */
  currentTier?: SubscriptionTier;
  /** Called when user taps "No, Thank you" in trial mode — lets parent show a nudge composer placeholder. */
  onDecline?: () => void;
  /** Current billing period so the plan card shows only the active price. Default 'monthly'. */
  currentPeriodType?: 'monthly' | 'annual';
  /** Trial expiration date for days-remaining display. */
  trialExpiresAt?: Date | null;
  /** Whether the trial has expired. */
  isTrialExpired?: boolean;
  /** Subscription expiration/renewal date for header display. */
  expiresAt?: Date | null;
  /** Subscription status for header context (active, cancelled, expired). */
  subscriptionStatus?: string | null;
  /** Whether the user is a beta tester with lifetime access. */
  isBetaTester?: boolean;
  /** Seven Free Days Wave 6: which RevenueCat placement this door is (spec §5).
   *  Only read when the server says `paywall_provider: 'revenuecat'`. */
  placement?: PaywallPlacement;
}

// ---------------------------------------------------------------------------
// Feature bullets are built inside the component (platform-aware)
// See getFeatures() defined at the top of the component body
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PaywallModal({
  visible,
  onClose,
  onPurchaseSuccess,
  showCloseButton = true,
  mode = 'trial',
  userId: userIdProp,
  currentTier = 'trial',
  onDecline,
  currentPeriodType = 'monthly',
  trialExpiresAt,
  isTrialExpired = false,
  expiresAt,
  subscriptionStatus,
  isBetaTester = false,
  placement = 'day7_stop',
}: PaywallModalProps) {
  const { user } = useAuth();
  const styles = useStyles();
  const faStyles = useFaStyles();
  const { palette } = useTheme();
  // The store's own prices, in this user's currency (the fine print's Essentials price).
  const storePrices = useSubscriptionPrices();
  // JWT-based fallback: when useAuth() hasn't populated `user` yet (common on
  // first session / Simulator), extract the userId from the Cedar JWT `sub` claim.
  const [jwtUserId, setJwtUserId] = useState<string | null>(null);
  useEffect(() => {
    if (user?.id) return; // Auth already loaded — no need for fallback
    (async () => {
      try {
        const token = await secureTokenStorage.getCedarJwt();
        if (!token) return;
        // ADVERSARIAL FIX: atob() doesn't exist in React Native (Hermes).
        const _b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const _decoded = Platform.OS === 'web' ? atob(_b64) : require('buffer').Buffer.from(_b64, 'base64').toString('utf-8');
        const payload = JSON.parse(_decoded);
        if (payload?.sub) {
          console.log('📦 PaywallModal: Extracted userId from JWT fallback:', payload.sub);
          setJwtUserId(payload.sub);
        }
      } catch (e) { console.warn('PaywallModal: Failed to parse JWT payload', e); }
    })();
  }, [user?.id]);
  const effectiveUserId = userIdProp ?? user?.id ?? jwtUserId;
  const insets = useSafeAreaInsets();
  const [trialPackage, setTrialPackage] = useState<PurchasesPackage | null>(null);
  /** Upgrade mode: all packages for Full / Extended, Monthly / Annual */
  const [allPackages, setAllPackages] = useState<PurchasesPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>(currentPeriodType);
  // S2-24: Sync billingPeriod if the parent updates currentPeriodType after initial render
  // (e.g. subscription hook async-loads the user's actual billing period)
  useEffect(() => {
    setBillingPeriod(currentPeriodType);
  }, [currentPeriodType]);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [showMorePlans, setShowMorePlans] = useState(false);
  /** Non-null when offerings could not be loaded (RevenueCat config or network issue). */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Guard against concurrent loadPackages() calls (e.g. rapid useEffect re-fires during auth init). */
  const isLoadingPackagesRef = useRef(false);
  /**
   * Always-current `mode` ref.  Because loadPackages() is async, the closure-captured
   * `mode` prop can become stale after an `await` (e.g. if the parent changes mode from
   * 'trial' → 'upgrade' while initialize() / getOfferings() are running, and the guard
   * blocks a second call).  Reading modeRef.current after each await gives us the value
   * that was in effect when the async call *finished*, not when it *started*.
   */
  const modeRef = useRef(mode);
  modeRef.current = mode; // Keep in sync on every render

  const showCloseButtonRef = useRef(showCloseButton);
  showCloseButtonRef.current = showCloseButton;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // 🤝 Partner offer sheet, a purchase that lands late (review of #4201, LOW-C5-a): the sheet can
  // report it AFTER it — and this modal, whose hosts unmount it when it closes — is gone. So its
  // callback reads the host's latest onPurchaseSuccess, and whether this paywall is still open,
  // from refs: never from a render that no longer exists.
  const onPurchaseSuccessRef = useRef(onPurchaseSuccess);
  onPurchaseSuccessRef.current = onPurchaseSuccess;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const paywallMountedRef = useRef(false);
  useEffect(() => {
    paywallMountedRef.current = true;
    return () => { paywallMountedRef.current = false; };
  }, []);

  // 🌞 Summer50 — "Have an invite code?" entry. Gated on the per-user flag.
  // iOS can't stack transparent modals, so we dismiss the paywall first, then
  // route to Settings which auto-opens the redeem drawer (?openDrawer=redeem_code).
  const summer50Router = useRouter();
  const { summer50, freeMonth, cohort, timeBoundTrial, lapsedPaid, paywallProvider, cedarCohort, paywallVariables, trialDaysLeft, trialLengthDays, partnerOffer, isLoading: statusLoading, statusUnavailable } = useSubscription();
  // 🤝 Partner codes C5 (design §4 step 4): a person holding a partner DISCOUNT sees the
  // partner offer sheet instead of either paywall. This modal mounts fresh on every open, so
  // its own status is still loading on the first render: on a phone the gate waits for it
  // (`checking`, a neutral spinner, at most 4 s) and decides once it is known (review of
  // #4115, H1). `none` for everyone who holds no live discount — for everyone while the
  // partner program is dark — and then nothing is fetched (hooks/usePartnerOfferGate.ts).
  const partnerGate = usePartnerOfferGate({
    visible,
    statusKnown: !statusLoading,
    statusUnavailable,
    partnerOffer,
    cedarCohort,
    timeBoundTrial,
  });
  // This modal's own status (review of #4062): until it has loaded, nothing says which
  // copy is true — the 7-day clock's, a store trial's, or a lapsed payer's. The status-
  // specific pieces (expired hero, its card, days left, the store-trial fine print)
  // stay neutral until it arrives; a failed first load keeps them neutral.
  const paywallStatusKnown = !statusLoading && !statusUnavailable;
  // Trial days remaining (for the active trial context card), in CALENDAR days, the
  // server's count first (review of #3988): the same resolver Account's one-liner uses,
  // so the two never disagree. Math.ceil over 24-hour units read "ends tomorrow" on the
  // last day and 8 on the first. 0 = the last day ("ends today"), 7 = the start.
  const trialDaysRemaining = resolveTrialDaysLeft({
    serverDaysLeft: trialDaysLeft,
    expiresAt: trialExpiresAt,
    clockTrial: timeBoundTrial,
    clockDays: trialLengthDays,
  });
  // No count of days until the status is known: the card says the neutral line.
  const shownTrialDaysRemaining = paywallStatusKnown ? trialDaysRemaining : null;
  // 🧭 Seven Free Days Wave 6 (Build B): when the SERVER selects the RevenueCat
  // provider for this build, this component presents RevenueCat's paywall for
  // `placement` instead of rendering itself, and runs the SAME post-purchase
  // glue. `legacy` (every build below B, web, the release-day fallback) renders
  // the modal below byte-for-byte. Guarded against double-presentation per open.
  const useRevenueCat = shouldUseRevenueCatPaywall(paywallProvider, cedarCohort);
  const rcPresentedForOpenRef = useRef(false);
  const [rcFallbackToLegacy, setRcFallbackToLegacy] = useState(false);
  useEffect(() => { if (!visible) setRcFallbackToLegacy(false); }, [visible]);
  useEffect(() => {
    if (!visible) { rcPresentedForOpenRef.current = false; return; }
    if (!useRevenueCat || rcPresentedForOpenRef.current) return;
    // The partner offer sheet owns this open while it is checked for or shown; if it falls
    // back ("See all plans", no Play option) the status turns `none` and this runs then.
    if (partnerGate.status !== 'none') return;
    rcPresentedForOpenRef.current = true;
    let cancelled = false;
    (async () => {
      const res = await presentRevenueCatPaywall({
        placement,
        cedarCohort,
        variables: paywallVariables,
        displayCloseButton: true,
      });
      if (cancelled) return;
      if (res.outcome === 'PURCHASED' || res.outcome === 'RESTORED') {
        // Same glue as the legacy modal: RevenueCat is canonical (webhook); the
        // backup activate is idempotent and the tier comes from the entitlement.
        let unlockTier: SubscriptionTier = 'full';
        try {
          const t = await subscriptionService.getCurrentTier();
          if (t === 'basic' || t === 'full' || t === 'extended') unlockTier = t;
        } catch { /* keep the safe default */ }
        await notifyBackendOfPurchase(unlockTier, 'monthly', {});
        onPurchaseSuccess?.(unlockTier);
        onClose();
        return;
      }
      if (res.outcome === 'NOT_PRESENTED' && res.reason === 'ui_unavailable') {
        // The module is missing on this shell after all — fall back to the
        // legacy modal for this open rather than showing nothing.
        rcPresentedForOpenRef.current = false;
        setRcFallbackToLegacy(true);
        return;
      }
      // CANCELLED / ERROR / NOT_PRESENTED (No Offering, cohort guard): closed.
      if (res.outcome === 'CANCELLED' || res.outcome === 'NOT_PRESENTED') onDecline?.();
      onClose();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, useRevenueCat, placement, partnerGate.status]);
  // 🕖 Seven Free Days (Wave 3, O1): the expired hero says "Your 7 days with
  // Cedar are done. Continue with Cedar." once the clock is on for this user;
  // the pre-sprint strings byte-for-byte otherwise.
  const expiredCopy = paywallStatusKnown ? paywallExpiredCopy({ timeBoundTrial, lapsedPaid, lengthDays: trialLengthDays }) : PAYWALL_EXPIRED_STATUS_UNKNOWN;
  // The card under the expired hero; none for a lapsed payer (review of #3967), and
  // none until the status is known.
  const expiredContextCopy = paywallStatusKnown ? paywallExpiredContextCopy({ timeBoundTrial, lapsedPaid }) : null;
  // 🌞 Summer50 — an ENDED (not converted) free month must never re-offer the
  // 7-day intro trial: prefer the `.notrial` twin packages when RevenueCat
  // serves them, and gracefully fall back to the standard products until the
  // ASC `.notrial` products + RC `no_trial` offering exist. Converted users
  // carry closed_reason='converted_to_paid' and stay on the standard offering.
  // A lapsed payer whose free month ended EARLIER reads the re-subscribe copy, never
  // "Your free access has ended" (follow-up to #3967): the same test the chat strip and
  // Account use.
  const preferNoTrial = cohort === 'subscription_expired' && freeMonth?.closed_reason === 'free_month_ended'
    && freeMonthEndedStillApplies(cohort, lapsedPaid);
  // 🌞 Free Access presentation (mock 2026-07-16): an ACTIVE free month shows
  // "Free Access + duration pill" instead of the raw tier ("CC Pro"/$39.99) —
  // the tier is plumbing, not the promise. Everything derives from grant dates.
  const freeMonthActive = !!freeMonth?.active;
  const freeMonthDeferred = !!freeMonth && !freeMonth.active && !freeMonth.closed_reason
    && !!freeMonth.starts_at && new Date(freeMonth.starts_at).getTime() > Date.now();
  const faEndDate = freeMonth?.expires_at
    ? new Date(freeMonth.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const faStartDate = freeMonth?.starts_at
    ? new Date(freeMonth.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const faPill = freeAccessPillLabel(freeMonth?.starts_at, freeMonth?.expires_at);
  const faProgress = freeMonthActive ? freeAccessProgress(freeMonth?.starts_at, freeMonth?.expires_at) : null;
  const handleOpenRedeem = () => {
    onClose();
    setTimeout(() => {
      try { summer50Router.push('/(tabs)/settings?openDrawer=redeem_code' as any); } catch { /* best-effort */ }
    }, 250);
  };

  // Swipe-down to close (upgrade drawer): only when showCloseButton so we're in drawer mode
  const swipeCloseThreshold = 50;
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_, gestureState) => {
        return gestureState.dy > 8;
      },
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dy > swipeCloseThreshold && showCloseButtonRef.current) {
          onCloseRef.current();
        }
      },
    })
  ).current;

  // -----------------------------------------------------------------------
  // Platform-aware feature bullets for trial mode
  // -----------------------------------------------------------------------
  const healthPlatformLabel = Platform.OS === 'ios' ? 'Apple Health' : 'Health Connect';
  const FEATURES = getPlanFeatureBullets(healthPlatformLabel);

  // -----------------------------------------------------------------------
  // Load packages: trial = full monthly only; upgrade = all packages
  // Retry when effectiveUserId becomes available (e.g. auth restored).
  // -----------------------------------------------------------------------

  // -----------------------------------------------------------------------
  // Early subscription check: if user is already subscribed when the modal
  // opens, release the gate immediately without requiring them to tap anything.
  // This eliminates the confusing "You're already subscribed" dialog.
  // -----------------------------------------------------------------------
  // NOTE: An early subscription check (auto-close if RevenueCat says subscribed)
  // was removed here because RevenueCat's cached CustomerInfo can be stale or
  // cross-contaminated between Sandbox users, causing an infinite loop:
  //   RevenueCat cache says "full" → onPurchaseSuccess → close modal →
  //   backend says "trial" → isTrial=true → re-open modal → loop.
  // The trial gate in ChatScreen already handles this correctly via the backend.

  useEffect(() => {
    if (visible) {
      // Reset the guard every time the modal opens so a previously-interrupted
      // load (e.g. the user dismissed mid-fetch) doesn't block the next attempt.
      isLoadingPackagesRef.current = false;
      setShowAllPlans(false);
      setBillingPeriod('monthly');
      loadPackages();
    } else {
      // Modal closing — release the mutex so the next open can start fresh.
      isLoadingPackagesRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPackages is stable (uses refs internally)
  }, [visible, mode, effectiveUserId]);

  async function loadPackages() {
    // Prevent concurrent calls (race condition guard).
    // This can happen when effectiveUserId transitions null→string during auth init,
    // causing the useEffect to fire twice in quick succession.
    if (isLoadingPackagesRef.current) {
      console.log('📦 PaywallModal: loadPackages() already in progress, skipping duplicate call');
      return;
    }
    isLoadingPackagesRef.current = true;

    setLoading(true);
    setTrialPackage(null);
    setAllPackages([]);
    setLoadError(null);

    // Guard: if userId not yet available, clear loading and release the guard so the
    // effect can retry when effectiveUserId becomes non-null. Otherwise we would leave
    // loading true and isLoadingPackagesRef true forever, so the UI stays on "Loading plans…"
    // and the next loadPackages() call is skipped by the "already in progress" check.
    if (!effectiveUserId) {
      console.warn('📦 PaywallModal: No userId yet (auth may still be loading) — waiting…');
      setLoading(false);
      isLoadingPackagesRef.current = false;
      return;
    }

    try {
        console.log('📦 PaywallModal: Initializing RevenueCat with userId:', effectiveUserId);
                await subscriptionService.initialize(effectiveUserId);

                // Give StoreKit/Play Billing a moment to fully resolve product metadata
                // (including locale-specific pricing) before the first offerings fetch.
                // Without this, cold-start fetches can return products whose priceString
                // is still a placeholder (e.g. "—") or formatted in USD before localization
                // has completed.
                await new Promise<void>((resolve) => setTimeout(resolve, 100));

                // StoreKit / Play Billing sometimes needs a moment to resolve products after
                // the app cold-starts. Retry up to 3 times with exponential back-off.
                // The exit condition checks that:
                //   1. At least one package was returned.
                //   2. The first package's priceString looks like a real price (not a dash
                //      placeholder, not empty) — confirming localization has completed.
                let packages: PurchasesPackage[] = [];
                const MAX_RETRIES = 3;
                for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                  packages = await subscriptionService.getOfferings();
                  console.log(`📦 PaywallModal: Loaded offerings (attempt ${attempt}/${MAX_RETRIES}):`, packages.length);

                  const firstPrice = packages[0]?.product?.priceString ?? '';
                  const priceResolved =
                    packages.length > 0 &&
                    firstPrice.length > 0 &&
                    firstPrice !== '—' &&
                    firstPrice !== '-';

                  if (priceResolved) {
                    console.log(`📦 PaywallModal: Prices resolved on attempt ${attempt} — ${firstPrice}`);
                    break;
                  }

                  if (attempt < MAX_RETRIES) {
                    const delay = attempt * 1500; // 1.5 s, 3 s
                    console.warn(
                      `⚠️ PaywallModal: Prices not yet localized (got "${firstPrice}") — retrying in ${delay}ms…`,
                    );
                    await new Promise<void>((resolve) => setTimeout(resolve, delay));
                  }
                }

      if (packages.length > 0) {
        packages.forEach((pkg) => {
          console.log(
            `💰 Package: ${pkg.identifier} | productId: ${pkg.product.identifier} | price: ${pkg.product.priceString} | currency: ${pkg.product.currencyCode}`,
          );
        });
      }

      if (packages.length === 0) {
        console.error('❌ PaywallModal: No packages returned after retries. Check that a "Current Offering" is configured in the RevenueCat dashboard and that products are approved in App Store Connect / Google Play.');
        setLoadError('Subscription options are temporarily unavailable. Please contact support if this persists.');
        return;
      }

      // Read the ref *after* all awaits so we always use the latest mode value,
      // not the stale one captured in the closure when loadPackages() was first called.
      const currentMode = modeRef.current;
      console.log('📦 PaywallModal: loadPackages resolving with mode =', currentMode, '| packages:', packages.length);

      if (currentMode === 'upgrade') {
        setAllPackages(packages);
      } else {
        // §1.1: Essentials is the trial entry tier. Users get Chat+ level access
        // during the 7-day trial, then roll into Essentials (its store price, never a typed one).
        // iOS and Android use different store product identifier formats —
        // getProductIdForPlatform resolves the correct one from TIER_CONFIG.
        const targetProductId = getProductIdForPlatform('basic', 'monthly', Platform.OS);

        if (!targetProductId) {
          console.error(
            `🚨 PaywallModal: No basic-monthly product configured for platform "${Platform.OS}".`,
          );
          setLoadError('Subscription options are temporarily unavailable. Please contact support if this persists.');
          return;
        }

        const byProductId = packages.find(
          (pkg) => pkg.product.identifier === targetProductId,
        );

        if (byProductId) {
          setTrialPackage(byProductId);
        } else {
          // No fallbacks — selecting the wrong package charges the wrong price.
          const availableIds = packages.map((p) => p.product.identifier);
          console.error(
            `🚨 PaywallModal: Product "${targetProductId}" not found in ${packages.length} packages. ` +
            `Available: ${availableIds.join(', ')}`,
          );
          // Close the instrumentation gap: post a diagnostic so CloudWatch captures
          // the actual identifiers returned by the store SDK on this device. The
          // existing _postDiagnostic hook only fires when offerings are missing
          // entirely — this path (offerings exist, target not found) was silent.
          void subscriptionService.reportTargetProductNotFound({
            target_product_id: targetProductId,
            available_product_ids: availableIds,
          });
          setLoadError('Subscription options are temporarily unavailable. Please contact support if this persists.');
        }
      }
    } catch (error) {
      console.error('Failed to load offerings', error);
      setLoadError('Unable to connect to the App Store. Please check your connection and try again.');
    } finally {
      setLoading(false);
      isLoadingPackagesRef.current = false; // Release guard so future calls (e.g. retry) can proceed
    }
  }

  // -----------------------------------------------------------------------
  // 🤝 Partner offer sheet: a purchase landed (partner codes C5; review of #4201, LOW-C5-a).
  // The same glue as the RevenueCat paywall: RevenueCat is canonical (webhook); the backup
  // activate is idempotent and the tier comes from the entitlement. MOUNT-INDEPENDENT: a given-up
  // attempt's purchase can land after the sheet — and this modal — has closed. It still runs the
  // backup subscription-activate and the host's onPurchaseSuccess (the trial-gate resume); it
  // closes the paywall only while it is still open (a closed one was closed already). The sheet
  // calls this at most once.
  // -----------------------------------------------------------------------
  async function handlePartnerPurchased(
    purchasedTier: 'basic' | 'full' | 'extended' | null,
    period: string,
  ) {
    let unlockTier: SubscriptionTier = purchasedTier ?? 'full';
    if (!purchasedTier) {
      try {
        const t = await subscriptionService.getCurrentTier();
        if (t === 'basic' || t === 'full' || t === 'extended') unlockTier = t;
      } catch { /* keep the safe default */ }
    }
    await notifyBackendOfPurchase(unlockTier, period, {});
    const stillOpen = paywallMountedRef.current && visibleRef.current;
    onPurchaseSuccessRef.current?.(unlockTier);
    if (stillOpen) onCloseRef.current();
  }

  // -----------------------------------------------------------------------
  // Notify backend of purchase (so Subscription Drawer shows correct state
  // immediately, without waiting for the RevenueCat webhook)
  // -----------------------------------------------------------------------

  async function notifyBackendOfPurchase(
    tier: SubscriptionTier,
    periodType: string = 'monthly',
    opts?: { isTrial?: boolean; trialEndMillis?: number },
  ) {
    try {
      const token = await secureTokenStorage.getCedarJwt();
      if (!token) {
        console.warn('⚠️ PaywallModal: No JWT — skipping backend subscription-activate');
        return;
      }
      const baseUrl = getApiBaseUrl();
      const payload: Record<string, unknown> = {
        tier,
        period_type: periodType,
        platform: Platform.OS,
      };
      // BUG FIX (Apr 2026): Pass trial state so the backend sets
      // subscription_status='trial' instead of 'active' when the App Store
      // purchase includes a free trial period.
      if (opts?.isTrial) {
        payload.is_trial = true;
        if (opts.trialEndMillis) payload.trial_end_millis = opts.trialEndMillis;
      }
      const res = await fetch(`${baseUrl}/api/user/subscription-activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        console.log('✅ PaywallModal: Backend notified of purchase — subscription-activate succeeded');
        // Clear any pending retry from a previous failed attempt
        await storage.removeItem('pending_subscription_activation');
      } else {
        console.warn('⚠️ PaywallModal: subscription-activate returned', res.status);
        // Persist for retry on next app launch (include trial metadata)
        await storage.setItem('pending_subscription_activation', JSON.stringify({
          tier, period_type: periodType, platform: Platform.OS, timestamp: Date.now(),
          is_trial: opts?.isTrial ?? false,
          trial_end_millis: opts?.trialEndMillis,
        }));
      }
    } catch (err) {
      // Network failure — persist for retry on next app launch (webhook may also handle it)
      console.warn('⚠️ PaywallModal: Failed to notify backend, persisting for retry', err);
      try {
        await storage.setItem('pending_subscription_activation', JSON.stringify({
          tier, period_type: periodType, platform: Platform.OS, timestamp: Date.now(),
          is_trial: opts?.isTrial ?? false,
          trial_end_millis: opts?.trialEndMillis,
        }));
      } catch (_) { /* storage failure is truly non-critical */ }
    }
  }

  // -----------------------------------------------------------------------
  // Purchase — start free trial (RevenueCat handles 7-day free period)
  // -----------------------------------------------------------------------

  async function handleStartTrial() {
    if (!trialPackage) {
      appAlert.show({ title: 'Loading...', message: 'Please wait while we load subscription options.' });
      return;
    }

    setPurchasing(true);

    try {
      const result = await subscriptionService.purchasePackage(trialPackage);

      if (result.success && result.tier) {
        // Immediately tell backend so Subscription Drawer reflects the purchase.
        // 🕖 Seven Free Days (Wave 3): pass is_trial only when the STORE says the
        // package starts a free trial (a free introductory price). On store day
        // the intro offer is retired (decision 1) and the same tap charges at
        // once; claiming a trial then would park the row in a phantom
        // status='trial' until the RevenueCat webhook corrected it.
        await notifyBackendOfPurchase(result.tier, 'monthly', { isTrial: packageStartsStoreTrial(trialPackage) });
        onPurchaseSuccess?.(result.tier);
        onClose();
      } else if (result.alreadySubscribed) {
        // RevenueCat says the product is already purchased — the user has an active
        // subscription that the app hasn't recognised yet.  Restore to get the latest
        // customerInfo; if that also fails or returns no tier, unlock as 'full' anyway
        // so the content gate is *always* released for a user who is already subscribed.
        console.log('ℹ️ PaywallModal: alreadySubscribed — restoring to sync entitlement state');
        let unlockTier: SubscriptionTier = 'full';
        try {
          const restoreResult = await subscriptionService.restorePurchases();
          if (restoreResult.success && restoreResult.tier) {
            unlockTier = restoreResult.tier;
          }
        } catch (restoreErr) {
          console.warn('⚠️ PaywallModal: Restore after alreadySubscribed failed (non-critical) — defaulting to full', restoreErr);
        }
        // Always unlock — the user is confirmed subscribed by StoreKit/Play Billing.
        // Only pass isTrial if we're at the trial gate (mode='trial') — an already-
        // subscribed user hitting the upgrade path should NOT be flagged as trial —
        // and only when the store still offers a free introductory period
        // (🕖 Seven Free Days: after store day nothing starts a trial).
        await notifyBackendOfPurchase(unlockTier, 'monthly', mode === 'trial' && packageStartsStoreTrial(trialPackage) ? { isTrial: true } : {});
        onPurchaseSuccess?.(unlockTier);
        onClose();
      } else if (result.cancelled) {
        // User cancelled the App Store / Play Store sheet — no error needed
        console.log('User cancelled purchase');
      } else {
        appAlert.error(
          'Something went wrong',
          result.error || 'Could not start your trial. Please try again.',
        );
      }
    } catch (error) {
      console.error('Purchase error', error);
      appAlert.error('Error', 'Could not start your trial. Please try again.');
    } finally {
      setPurchasing(false);
    }
  }

  // -----------------------------------------------------------------------
  // Upgrade mode — purchase a specific package (Full or Extended)
  // -----------------------------------------------------------------------

  async function handlePurchasePackage(pkg: PurchasesPackage) {
    setPurchasing(true);
    try {
      const result = await subscriptionService.purchasePackage(pkg);
      // Infer period from package identifier
      const periodType = pkg.identifier.toLowerCase().includes('annual') ? 'annual' : 'monthly';
      if (result.success && result.tier) {
        await notifyBackendOfPurchase(result.tier, periodType);
        onPurchaseSuccess?.(result.tier);
        onClose();
      } else if (result.alreadySubscribed) {
        // RevenueCat says the product is already purchased. Restore purchases to
        // sync the latest entitlement state, then always call onPurchaseSuccess so
        // the content gate is released regardless of whether the restore can name
        // the exact tier.
        console.log('ℹ️ PaywallModal: alreadySubscribed (upgrade) — restoring to sync entitlement state');
        let unlockTier: SubscriptionTier = 'full';
        try {
          const restoreResult = await subscriptionService.restorePurchases();
          if (restoreResult.success && restoreResult.tier) {
            unlockTier = restoreResult.tier;
          }
        } catch (restoreErr) {
          console.warn('⚠️ PaywallModal: Restore after alreadySubscribed failed (non-critical) — defaulting to full', restoreErr);
        }
        // Always unlock — StoreKit / Play Billing confirmed the user is subscribed.
        await notifyBackendOfPurchase(unlockTier, periodType);
        onPurchaseSuccess?.(unlockTier);
        onClose();
      } else if (!result.cancelled) {
        appAlert.error(
          'Something went wrong',
          result.error || 'Could not complete purchase. Please try again.',
        );
      }
    } catch (error) {
      console.error('Purchase error', error);
      appAlert.error('Error', 'Could not complete purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  }

  // Helper: match on RevenueCat package identifier OR App Store product identifier
  const pkgMatches = (p: PurchasesPackage, tier: string) => {
    const pkgId = p.identifier.toLowerCase();
    const prodId = p.product.identifier.toLowerCase();
    return pkgId.includes(tier) || prodId.includes(tier);
  };

  // 🌞 Summer50 — once the RC `no_trial` offering exists, its `.notrial` twins
  // land in the SAME flattened package list (getOfferings collects all
  // offerings). Partition BOTH directions: standard users must never resolve a
  // `.notrial` package (they'd silently lose their 7-day intro trial at the
  // Apple sheet); the ended-free-month cohort prefers `.notrial` and falls back
  // to the standard product while the twins don't exist yet.
  const isNoTrialPkg = (p: PurchasesPackage) => p.product.identifier.toLowerCase().includes('.notrial');
  const pickPkg = (matches: (p: PurchasesPackage) => boolean): PurchasesPackage | undefined => {
    const standard = allPackages.find((p) => matches(p) && !isNoTrialPkg(p));
    if (!preferNoTrial) return standard;
    return allPackages.find((p) => matches(p) && isNoTrialPkg(p)) ?? standard;
  };

  // Partition allPackages into Basic (monthly/annual), Full (monthly/annual) and Extended (monthly/annual)
  const basicMonthly = pickPkg((p) => pkgMatches(p, 'basic') && !pkgMatches(p, 'annual'));
  const basicAnnual = pickPkg((p) => pkgMatches(p, 'basic') && pkgMatches(p, 'annual'));
  const fullMonthly = pickPkg((p) => pkgMatches(p, 'full') && !pkgMatches(p, 'annual') && !pkgMatches(p, 'extended'));
  const fullAnnual = pickPkg((p) => pkgMatches(p, 'full') && pkgMatches(p, 'annual'));
  const extendedMonthly = pickPkg((p) => pkgMatches(p, 'extended') && !pkgMatches(p, 'annual'));
  const extendedAnnual = pickPkg((p) => pkgMatches(p, 'extended') && pkgMatches(p, 'annual'));

  // Debug: log package resolution so we can diagnose missing tiers
  if (allPackages.length > 0) {
    console.log('📦 PaywallModal: Package resolution:', {
      preferNoTrial,
      basicMonthly: basicMonthly?.product.identifier ?? 'NOT FOUND',
      basicAnnual: basicAnnual?.product.identifier ?? 'NOT FOUND',
      fullMonthly: fullMonthly?.product.identifier ?? 'NOT FOUND',
      fullAnnual: fullAnnual?.product.identifier ?? 'NOT FOUND',
      extendedMonthly: extendedMonthly?.product.identifier ?? 'NOT FOUND',
      extendedAnnual: extendedAnnual?.product.identifier ?? 'NOT FOUND',
      allPackageIds: allPackages.map((p) => `${p.identifier} (${p.product.identifier})`),
    });
  }
  const isUpgradeInteractive = !purchasing && !loading && allPackages.length > 0;

  // -----------------------------------------------------------------------
  // Restore purchases
  // -----------------------------------------------------------------------

  async function handleRestore() {
    setLoading(true);

    try {
      const result = await subscriptionService.restorePurchases();

      if (result.success) {
        // Resolve tier — fall back to 'full' if service didn't return one
        const restoredTier: SubscriptionTier = result.tier ?? 'full';

        // 🔧 FIX: Sync restored tier with the backend so Settings/subscription
        // status reflects the restored subscription (previously only the paywall
        // dismissed but the backend still showed 'trial').
        await notifyBackendOfPurchase(restoredTier);

        appAlert.show({
          title: 'Welcome back!',
          message: 'Your subscription has been restored.',
          buttons: [
            {
              text: 'Continue',
              onPress: () => {
                onPurchaseSuccess?.(restoredTier);
                onClose();
              },
            },
          ],
        });
      } else {
        appAlert.show({ title: 'No Purchases Found', message: 'No active subscriptions found to restore.' });
      }
    } catch (error) {
      console.error('Restore error', error);
      appAlert.error('Error', 'Could not restore purchases. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /** Open device subscription management (App Store / Play Store). Used for Cancel and Downgrade. */
  function openSubscriptionSettings() {
    if (Platform.OS === 'ios') {
      Linking.openURL('https://apps.apple.com/account/subscriptions');
    } else if (Platform.OS === 'android') {
      Linking.openURL('https://play.google.com/store/account/subscriptions');
    } else {
      appAlert.show({
        title: 'Manage Subscription',
        message: 'Manage your subscription through the iOS App Store or Google Play Store',
      });
    }
  }

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  // priceString comes from App Store / Google Play via RevenueCat — automatically
  // localized to the user's region and currency (e.g. $34.99 CAD, $24.99 USD).
  // Falls back to a loading indicator while packages are fetching.
  const priceString = trialPackage?.product?.priceString ?? (loading ? '…' : '—');
  const isInteractive = !purchasing && !loading && !!trialPackage;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!visible) return null;
  // 🤝 Partner codes C5: not decided yet whether this open is the partner offer sheet (the
  // status, or the server, is still being read) — a neutral spinner for that moment, never
  // the wrong paywall (at most PARTNER_GATE_TIMEOUT_MS).
  if (partnerGate.status === 'checking') {
    return (
      <View style={[styles.container, styles.partnerChecking, { paddingTop: insets.top }]} testID="partner-gate-checking">
        <CanvasGradient topInset={insets.top} />
        <ActivityIndicator color={palette.ink.accent} />
      </View>
    );
  }
  if (partnerGate.status === 'show' && partnerGate.offer) {
    return (
      <PartnerOfferSheet
        offer={partnerGate.offer}
        userId={effectiveUserId ?? null}
        onSeeAllPlans={partnerGate.fallBack}
        // "Not now" closes; the decline is recorded by the sheet, which holds it back while a
        // given-up purchase may still land (a buyer never declined — LOW-C5-a).
        onNotNow={onClose}
        onDecline={onDecline}
        onPurchased={handlePartnerPurchased}
      />
    );
  }
  // RevenueCat owns this open: nothing to render (the native sheet is up).
  if (useRevenueCat && !rcFallbackToLegacy) return null;

  return (
    <View style={[styles.container, { paddingTop: insets.top }]} accessibilityViewIsModal={true} accessibilityLabel={mode === 'upgrade' ? 'Dialog: Your Coach Cedar plan' : 'Dialog: Start your free trial'}>
        {/* The page ground every tab already stands on. `topInset` is not
            optional here: this container pads by `insets.top`, and Yoga insets
            absolute children by their parent's padding — without it the
            gradient starts BELOW the status bar and leaves an unpainted strip
            exactly where its white end is supposed to be. */}
        <CanvasGradient topInset={insets.top} />
        {mode === 'upgrade' ? (
          <>
            {/* ── Header: handle + title; swipe down anywhere here to close ── */}

            <View style={styles.headerGreenWrapper}>
              <View style={styles.headerBanner}>
                <Text style={styles.headerBannerTitle}>
                  {/* 🌞 Summer50 G4/G5 — an ended free month is NOT an ended trial;
                      say what actually happened ("Your free month is over"). */}
                  {freeMonthActive
                    ? S50.FA_paywall_title
                    : preferNoTrial
                    ? S50.G4_paywall_title
                    : currentTier === 'trial' && isTrialExpired
                    ? expiredCopy.title
                    : currentTier === 'trial'
                    ? "You're on a free trial"
                    : 'Your Coach Cedar plan'}
                </Text>
                <Text style={styles.headerBannerSubtitle}>
                  {freeMonthActive
                    ? `Free through ${faEndDate}`
                    : preferNoTrial
                    ? S50.G5_paywall_body
                    : currentTier === 'trial' && isTrialExpired
                    ? expiredCopy.subtitle
                    : currentTier === 'trial'
                    ? paywallActiveTrialSubtitle({ timeBoundTrial, lengthDays: trialLengthDays })
                    : (() => {
                        const tierName = currentTier === 'extended' ? 'CC Pro' : currentTier === 'full' ? 'CC Chat+' : 'CC Essentials';
                        if (isBetaTester) return `${tierName} · Lifetime access`;
                        // IAP trial: the user purchased a paid tier that's still in
                        // the 7-day free period. Show "Trial ends" with the trial
                        // expiration date so they don't think they're already being
                        // billed (subscription_ends_at points 30+ days out).
                        if (subscriptionStatus === 'trial') {
                          const trialDate = trialExpiresAt || expiresAt;
                          const trialDateStr = trialDate
                            ? new Date(trialDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : null;
                          return trialDateStr ? `${tierName} · Trial ends ${trialDateStr}` : `${tierName} · Free trial`;
                        }
                        const dateStr = expiresAt ? new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                        if (!dateStr) return tierName;
                        if (subscriptionStatus === 'cancelled') return `${tierName} · Access until ${dateStr}`;
                        return `${tierName} · Renews ${dateStr}`;
                      })()}
                </Text>
              </View>
              {/* X close button — top right */}
              <Pressable
                style={styles.headerCloseButton}
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel="Close"
                hitSlop={12}
              >
                {({ pressed }) => (
                  <Text style={[styles.headerCloseButtonText, pressed && { transform: [{ scale: 0.8 }], opacity: 0.5 }]}>×</Text>
                )}
              </Pressable>
            </View>

            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              {loading && (
                <Text style={styles.loadingPlansText}>Loading plans…</Text>
              )}
              {!loading && allPackages.length === 0 && (
                <View style={styles.emptyPlansBlock}>
                  {Platform.OS === 'web' ? (
                    <>
                      <Text style={styles.loadingPlansText}>
                        Subscriptions are available in the Coach Cedar app. Download on the App Store or Google Play to start your free trial or upgrade.
                      </Text>
                      <View style={styles.storeLinksRow}>
                        <Pressable
                          style={({ pressed }) => [styles.storeLinkButton, pressed && styles.storeLinkButtonPressed]}
                          onPress={() => Linking.openURL('https://apps.apple.com/app/coach-cedar/id6758776684')}
                        >
                          <Text style={styles.storeLinkText}>App Store</Text>
                        </Pressable>
                        <Pressable
                          style={({ pressed }) => [styles.storeLinkButton, pressed && styles.storeLinkButtonPressed]}
                          onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.coachcedar.app')}
                        >
                          <Text style={styles.storeLinkText}>Google Play</Text>
                        </Pressable>
                      </View>
                    </>
                  ) : (
                    <>
                      <Text style={styles.loadErrorText}>
                        {loadError ?? 'Subscription options are temporarily unavailable. Please try again later.'}
                      </Text>
                      <AnimatedPressable
                        variant="standard"
                        style={({ pressed }) => [styles.retryButton, pressed && styles.retryButtonPressed]}
                        onPress={() => {
                          setLoadError(null);
                          setLoading(true);
                          isLoadingPackagesRef.current = false;
                          loadPackages();
                        }}
                      >
                        <Text style={styles.retryButtonText}>Try Again</Text>
                      </AnimatedPressable>
                    </>
                  )}
                </View>
              )}
              {/* 🌞 Free Access card — replaces the raw paid-tier card for an
                  active free month (no price, no Active badge, no cancel). */}
              {freeMonthActive && !loading && (
                <View style={styles.currentPlanBlock}>
                  <Text style={styles.currentPlanSectionLabel}>{S50.FA_section}</Text>
                  <View style={[styles.currentPlanCard, { flexDirection: 'column' }]}>
                    <LinearGradient
                      colors={[...palette.glass.buttonGradient.colors]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 0, y: 1 }}
                      style={RAMP_FILL}
                      pointerEvents="none"
                    />
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Text style={styles.currentPlanName}>{S50.FA_card_name}</Text>
                      <View style={faStyles.pill}><Text style={faStyles.pillText}>{faPill}</Text></View>
                    </View>
                    <Text style={faStyles.line}>{S50.FA_line(faEndDate)}</Text>
                    <View style={{ marginTop: 4, ...palette.material.dataPanel, borderRadius: 8, padding: 12, width: '100%' }}>
                      {[S50.FA_bullet1, S50.FA_bullet2].map((item, i) => (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i === 0 ? 6 : 0 }}>
                          <Text style={{ fontSize: 13, color: themed(palette, '#444', palette.ink.primary), lineHeight: 19, marginRight: 6 }}>{'\u2022'}</Text>
                          <Text style={{ fontSize: 13, color: themed(palette, '#444', palette.ink.primary), lineHeight: 19, flex: 1 }}>{item}</Text>
                        </View>
                      ))}
                    </View>
                    {faProgress && (
                      <View style={{ width: '100%', marginTop: 12 }}>
                        <View style={faStyles.meterBar}>
                          <View style={[faStyles.meterFill, { width: `${Math.round(faProgress.fraction * 100)}%` }]} />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                          <Text style={faStyles.meterCap}>{S50.FA_day_of(faProgress.dayOf, faProgress.totalDays)}</Text>
                          <Text style={faStyles.meterCap}>{S50.FA_days_left(faProgress.daysLeft)}</Text>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              )}
              {/* Current plan info — for paid subscribers */}
              {!freeMonthActive && (currentTier === 'basic' || currentTier === 'full' || currentTier === 'extended') && !loading && (() => {
                const tierCfg = getTierConfig(currentTier);
                const monthly = currentTier === 'extended' ? extendedMonthly : currentTier === 'full' ? fullMonthly : basicMonthly;
                const annual = currentTier === 'extended' ? extendedAnnual : currentTier === 'full' ? fullAnnual : basicAnnual;
                return (
                  <View style={styles.currentPlanBlock}>
                    <Text style={styles.currentPlanSectionLabel}>Your current plan</Text>
                    <View style={[styles.currentPlanCard, { flexDirection: 'column' }]}>
                    <LinearGradient
                      colors={[...palette.glass.buttonGradient.colors]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 0, y: 1 }}
                      style={RAMP_FILL}
                      pointerEvents="none"
                    />
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <View>
                          <Text style={styles.currentPlanName}>
                            {currentPeriodType === 'annual' ? 'Annual' : 'Monthly'}
                          </Text>
                          <Text style={styles.currentPlanPrice}>
                            {currentPeriodType === 'annual' && annual
                              ? `${annual.product?.priceString ?? '—'}/yr`
                              : `${monthly?.product?.priceString ?? (loading ? '…' : '—')}/mo`}
                          </Text>
                        </View>
                        <View style={styles.currentPlanActiveBadge}>
                          <Text style={styles.currentPlanActiveBadgeText}>Active ✓</Text>
                        </View>
                      </View>
                      {/* Plan details — white container inside card */}
                      <View style={{ marginTop: 12, ...palette.material.dataPanel, borderRadius: 8, padding: 12, width: '100%' }}>
                        {tierCfg.differentiators.map((item, i) => (
                          <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < tierCfg.differentiators.length - 1 ? 6 : 0 }}>
                            <Text style={{ fontSize: 13, color: themed(palette, '#444', palette.ink.primary), lineHeight: 19, marginRight: 6 }}>{'\u2022'}</Text>
                            <Text style={{ fontSize: 13, color: themed(palette, '#444', palette.ink.primary), lineHeight: 19, flex: 1 }}>{item}</Text>
                          </View>
                        ))}
                      </View>
                    </View>

                    {/* Switch billing period — subtle, doesn't overpower the card */}
                    {currentPeriodType === 'monthly' && annual && (
                      <Pressable
                        style={({ pressed }) => [{
                          marginTop: 4,
                          paddingVertical: 12,
                          paddingHorizontal: 14,
                          borderRadius: 8,
                          backgroundColor: palette.surface.well,
                          alignItems: 'center' as const,
                          opacity: pressed ? 0.7 : 1,
                        }]}
                        onPress={() => openSubscriptionSettings()}
                      >
                        <Text style={{ fontSize: 13, fontWeight: '500', color: palette.ink.accentSoft }}>
                          Switch to Annual — {annual.product?.priceString ?? '—'}/yr
                        </Text>
                      </Pressable>
                    )}
                    {currentPeriodType === 'annual' && monthly && (
                      <Pressable
                        style={({ pressed }) => [{
                          marginTop: 4,
                          paddingVertical: 12,
                          paddingHorizontal: 14,
                          borderRadius: 8,
                          backgroundColor: palette.surface.well,
                          alignItems: 'center' as const,
                          opacity: pressed ? 0.7 : 1,
                        }]}
                        onPress={() => openSubscriptionSettings()}
                      >
                        <Text style={{ fontSize: 13, fontWeight: '500', color: palette.ink.accentSoft }}>
                          Switch to Monthly — {monthly.product?.priceString ?? '—'}/mo
                        </Text>
                      </Pressable>
                    )}
                  </View>
                );
              })()}

              {/* ── TRIAL CONTEXT CARD: warm messaging for active/expired trial ── */}
              {currentTier === 'trial' && !loading && !isTrialExpired && (
                <View style={styles.trialContextCard}>
                  <Text style={styles.trialContextTitle}>We hope you're enjoying Coach Cedar</Text>
                  <Text style={styles.trialContextBody}>
                    {shownTrialDaysRemaining === null
                      ? "Here's what stays with you when you subscribe:"
                      : shownTrialDaysRemaining === 0
                      ? "Your trial ends today. Subscribe now to keep everything:"
                      : shownTrialDaysRemaining === 1
                      ? "Your trial ends tomorrow. Don't lose your progress — here's what stays with you:"
                      : shownTrialDaysRemaining <= 3
                      ? `Your trial ends in ${shownTrialDaysRemaining} days. Don't lose your progress — here's what stays with you:`
                      : shownTrialDaysRemaining >= 7
                      ? "Your trial has just begun. Here's what stays with you when you subscribe:"
                      : `Your trial ends in ${shownTrialDaysRemaining} days. Here's what stays with you when you subscribe:`}
                  </Text>
                  <View style={styles.trialContextBenefits}>
                    {[
                      'Personalized plans that adapt weekly to your progress',
                      'Post-workout debriefs with Coach Cedar',
                      'Morning check-ins and nudges',
                      'Weekly Coach Cedar Report with insights',
                    ].map((benefit, i) => (
                      <View key={i} style={styles.trialContextBenefitRow}>
                        <Text style={styles.trialContextCheck}>✓</Text>
                        <Text style={styles.trialContextBenefitText}>{benefit}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
              {currentTier === 'trial' && !loading && isTrialExpired && expiredContextCopy && (
                <View style={styles.expiredContextCard}>
                  <Text style={styles.expiredContextTitle}>{expiredContextCopy.title}</Text>
                  <Text style={styles.expiredContextBody}>{expiredContextCopy.body}</Text>
                </View>
              )}

              {/* ── TRIAL USER: focused Chat+ with toggle + expandable other plans ── */}
              {currentTier === 'trial' && !loading && allPackages.length > 0 && (
                <>
                  {/* Monthly / Annual pill toggle */}
                  <View style={styles.billingToggleRow}>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'monthly' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('monthly')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'monthly' && styles.billingToggleTextActive]}>Monthly</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'annual' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('annual')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'annual' && styles.billingToggleTextActive]}>Annual</Text>
                    </Pressable>
                  </View>

                  {/* Recommended: Chat+ single CTA */}
                  {(fullMonthly || fullAnnual) && (() => {
                    const chatPlusPkg = billingPeriod === 'annual' ? (fullAnnual ?? fullMonthly) : (fullMonthly ?? fullAnnual);
                    const chatPlusCfg = getTierConfig('full');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>
                          CC Chat+ — {chatPlusPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}
                        </Text>
                        <View style={styles.trialPlanBulletsCard}>
                          {chatPlusCfg.differentiators.map((item, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < chatPlusCfg.differentiators.length - 1 ? 4 : 0 }}>
                              <Text style={styles.trialPlanBullet}>{'\u2022'}</Text>
                              <Text style={styles.trialPlanBulletText}>{item}</Text>
                            </View>
                          ))}
                        </View>
                        <AnimatedPressable
                          variant="cta"
                          themed
                          style={({ pressed }) => [
                            styles.trialCtaButton,
                            !isUpgradeInteractive && styles.upgradeCardDisabled,
                            pressed && isUpgradeInteractive && styles.trialCtaButtonPressed,
                          ]}
                          onPress={() => chatPlusPkg && handlePurchasePackage(chatPlusPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          {purchasing ? (
                            <ActivityIndicator color={palette.ink.onCta} />
                          ) : (
                            <Text style={styles.trialCtaButtonText}>
                              {mode === 'upgrade' && currentTier === 'trial' && isTrialExpired
                                ? expiredCopy.cta
                                : mode === 'upgrade' && currentTier === 'trial'
                                ? 'Upgrade to Chat+'
                                : 'Start free trial'}
                            </Text>
                          )}
                        </AnimatedPressable>
                      </View>
                    );
                  })()}

                  {/* See all plans toggle */}
                  <Pressable
                    style={({ pressed }) => [styles.seeAllPlansButton, pressed && styles.seeAllPlansButtonPressed]}
                    onPress={() => setShowAllPlans(!showAllPlans)}
                  >
                    <Text style={styles.seeAllPlansText}>
                      {showAllPlans ? 'Hide other plans' : 'See all plans'}
                    </Text>
                    <NativeIcon name={showAllPlans ? 'chevron.up' : 'chevron.down'} size={14} color={palette.ink.accent} weight="semibold" />
                  </Pressable>

                  {/* Expanded: Pro */}
                  {showAllPlans && (extendedMonthly || extendedAnnual) && (() => {
                    const proPkg = billingPeriod === 'annual' ? (extendedAnnual ?? extendedMonthly) : (extendedMonthly ?? extendedAnnual);
                    const proCfg = getTierConfig('extended');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>
                          CC Pro — {proPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}
                        </Text>
                        <View style={styles.trialPlanBulletsCard}>
                          {proCfg.differentiators.map((item, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < proCfg.differentiators.length - 1 ? 4 : 0 }}>
                              <Text style={styles.trialPlanBullet}>{'\u2022'}</Text>
                              <Text style={styles.trialPlanBulletText}>{item}</Text>
                            </View>
                          ))}
                        </View>
                        <AnimatedPressable
                          variant="standard"
                          style={({ pressed }) => [
                            styles.upgradeCard,
                            !isUpgradeInteractive && styles.upgradeCardDisabled,
                            pressed && isUpgradeInteractive && styles.upgradeCardPressed,
                          ]}
                          onPress={() => proPkg && handlePurchasePackage(proPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          <CtaGradient />
                          <View style={styles.upgradeCardLeft}>
                            <Text style={styles.upgradeCardTitle}>{billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Text>
                            <Text style={styles.upgradeCardPrice}>{proPkg?.product?.priceString ?? '—'}</Text>
                          </View>
                          <Text style={styles.upgradeCardCta}>
                            {mode === 'upgrade' && currentTier === 'trial' && isTrialExpired
                              ? expiredCopy.cardCta
                              : mode === 'upgrade' && currentTier === 'trial'
                              ? 'Upgrade to Pro'
                              : 'Start free trial'}
                          </Text>
                        </AnimatedPressable>
                      </View>
                    );
                  })()}

                  {/* Expanded: Essentials (monthly-only — hidden when annual toggle active) */}
                  {showAllPlans && billingPeriod !== 'annual' && (basicMonthly || basicAnnual) && (() => {
                    const essentialsPkg = basicMonthly ?? basicAnnual;
                    const essentialsCfg = getTierConfig('basic');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>
                          CC Essentials — {essentialsPkg?.product?.priceString ?? '—'}/month
                        </Text>
                        <View style={styles.trialPlanBulletsCard}>
                          {essentialsCfg.differentiators.map((item, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < essentialsCfg.differentiators.length - 1 ? 4 : 0 }}>
                              <Text style={styles.trialPlanBullet}>{'\u2022'}</Text>
                              <Text style={styles.trialPlanBulletText}>{item}</Text>
                            </View>
                          ))}
                        </View>
                        <AnimatedPressable
                          variant="standard"
                          style={({ pressed }) => [
                            styles.upgradeCard,
                            !isUpgradeInteractive && styles.upgradeCardDisabled,
                            pressed && isUpgradeInteractive && styles.upgradeCardPressed,
                          ]}
                          onPress={() => essentialsPkg && handlePurchasePackage(essentialsPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          <CtaGradient />
                          <View style={styles.upgradeCardLeft}>
                            <Text style={styles.upgradeCardTitle}>Monthly</Text>
                            <Text style={styles.upgradeCardPrice}>{essentialsPkg?.product?.priceString ?? '—'}</Text>
                          </View>
                          <Text style={styles.upgradeCardCta}>
                            {mode === 'upgrade' && currentTier === 'trial' && isTrialExpired
                              ? expiredCopy.cardCta
                              : mode === 'upgrade' && currentTier === 'trial'
                              ? 'Switch to Essentials'
                              : 'Start free trial'}
                          </Text>
                        </AnimatedPressable>
                      </View>
                    );
                  })()}
                </>
              )}

              {/* ── PAID USERS: "OTHER PLANS" dropdown toggle + expandable plans ── */}

              {/* OTHER PLANS toggle — tappable label with chevron for all paid tiers */}
              {(currentTier === 'basic' || currentTier === 'full' || currentTier === 'extended') && !loading && (
                <Pressable
                  style={({ pressed }) => [styles.otherPlansToggle, pressed && styles.otherPlansTogglePressed]}
                  onPress={() => setShowAllPlans(!showAllPlans)}
                >
                  <Text style={styles.otherPlansSectionLabel}>{freeMonthActive ? S50.FA_plans_toggle : 'Other plans'}</Text>
                  <NativeIcon name={showAllPlans ? 'chevron.up' : 'chevron.down'} size={12} color={palette.ink.secondary} weight="semibold" />
                </Pressable>
              )}

              {/* 🌞 FREE ACCESS: neutral plan options — informational only.
                  A free-access user has NO store subscription, so the paid-tier
                  branches below are wrong for them twice over: "Downgrade"
                  framing assumes they're on Pro, and openSubscriptionSettings()
                  routes to an App Store page with nothing on it. Purchase is
                  also deliberately absent: Apple can't schedule a future-start
                  subscription, so buying now would either burn remaining gift
                  days (.notrial) or attach the 7-day intro trial mid-gift
                  (standard). Selling happens at close-out via the ended-state
                  paywall. */}
              {freeMonthActive && showAllPlans && (
                <>
                  <View style={styles.billingToggleRow}>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'monthly' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('monthly')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'monthly' && styles.billingToggleTextActive]}>Monthly</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'annual' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('annual')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'annual' && styles.billingToggleTextActive]}>Annual</Text>
                    </Pressable>
                  </View>
                  {/* Essentials — monthly-only product, hidden on annual */}
                  {billingPeriod !== 'annual' && (
                    <View style={faStyles.planCard}>
                      <View style={faStyles.planHead}>
                        <Text style={faStyles.planName}>CC Essentials</Text>
                        <Text style={faStyles.planPrice}>
                          {basicMonthly?.product?.priceString ?? '—'}
                          <Text style={faStyles.planPer}> /mo</Text>
                        </Text>
                      </View>
                      <Text style={faStyles.planDesc}>{S50.FA_plans_desc_basic}</Text>
                    </View>
                  )}
                  <View style={faStyles.planCard}>
                    <View style={faStyles.planHead}>
                      <Text style={faStyles.planName}>CC Chat+</Text>
                      <Text style={faStyles.planPrice}>
                        {(billingPeriod === 'annual' ? fullAnnual : fullMonthly)?.product?.priceString ?? '—'}
                        <Text style={faStyles.planPer}>{billingPeriod === 'annual' ? ' /yr' : ' /mo'}</Text>
                      </Text>
                    </View>
                    <Text style={faStyles.planDesc}>{S50.FA_plans_desc_full}</Text>
                    <View style={faStyles.planLevelTag}>
                      <Text style={faStyles.planLevelTagText}>{S50.FA_plans_level_tag}</Text>
                    </View>
                  </View>
                  <View style={faStyles.planCard}>
                    <View style={faStyles.planHead}>
                      <Text style={faStyles.planName}>CC Pro</Text>
                      <Text style={faStyles.planPrice}>
                        {(billingPeriod === 'annual' ? extendedAnnual : extendedMonthly)?.product?.priceString ?? '—'}
                        <Text style={faStyles.planPer}>{billingPeriod === 'annual' ? ' /yr' : ' /mo'}</Text>
                      </Text>
                    </View>
                    <Text style={faStyles.planDesc}>{S50.FA_plans_desc_extended}</Text>
                  </View>
                  <Text style={faStyles.planFoot}>{S50.FA_plans_foot(faEndDate)}</Text>
                </>
              )}

              {/* ── FROM PRO: billing toggle + Chat+ downgrade + Essentials downgrade ── */}
              {!freeMonthActive && currentTier === 'extended' && showAllPlans && (
                <>
                  {/* Billing period toggle */}
                  <View style={styles.billingToggleRow}>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'monthly' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('monthly')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'monthly' && styles.billingToggleTextActive]}>Monthly</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'annual' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('annual')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'annual' && styles.billingToggleTextActive]}>Annual</Text>
                    </Pressable>
                  </View>

                  {(fullMonthly || fullAnnual) && (() => {
                    const chatPlusPkg = billingPeriod === 'annual' ? (fullAnnual ?? fullMonthly) : (fullMonthly ?? fullAnnual);
                    const chatPlusCfg = getTierConfig('full');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>CC Chat+ — {chatPlusPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}</Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, styles.downgradeCard, { flexDirection: 'column' }, pressed && styles.downgradeCardPressed]}
                          onPress={() => openSubscriptionSettings()}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={[styles.upgradeCardTitle, styles.downgradeCardText]}>{billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Text>
                              <Text style={[styles.upgradeCardPrice, styles.downgradeCardText]}>{chatPlusPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={[styles.upgradeCardCta, styles.downgradeCardText]}>Downgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {chatPlusCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < chatPlusCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}
                  {/* Essentials downgrade (monthly-only — hidden when annual toggle active) */}
                  {billingPeriod !== 'annual' && (basicMonthly || basicAnnual) && (() => {
                    const essentialsPkg = basicMonthly ?? basicAnnual;
                    const essentialsCfg = getTierConfig('basic');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>CC Essentials — {essentialsPkg?.product?.priceString ?? '—'}/month</Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, styles.downgradeCard, { flexDirection: 'column' }, pressed && styles.downgradeCardPressed]}
                          onPress={() => openSubscriptionSettings()}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={[styles.upgradeCardTitle, styles.downgradeCardText]}>Monthly</Text>
                              <Text style={[styles.upgradeCardPrice, styles.downgradeCardText]}>{essentialsPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={[styles.upgradeCardCta, styles.downgradeCardText]}>Downgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {essentialsCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < essentialsCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}
                </>
              )}

              {/* ── FROM CHAT+: billing toggle + Pro upgrade + Essentials downgrade ── */}
              {!freeMonthActive && currentTier === 'full' && showAllPlans && (
                <>
                  {/* Billing period toggle */}
                  <View style={styles.billingToggleRow}>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'monthly' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('monthly')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'monthly' && styles.billingToggleTextActive]}>Monthly</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'annual' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('annual')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'annual' && styles.billingToggleTextActive]}>Annual</Text>
                    </Pressable>
                  </View>

                  {(extendedMonthly || extendedAnnual) && (() => {
                    const proPkg = billingPeriod === 'annual' ? (extendedAnnual ?? extendedMonthly) : (extendedMonthly ?? extendedAnnual);
                    const proCfg = getTierConfig('extended');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>CC Pro — {proPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}</Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, { flexDirection: 'column' }, pressed && styles.upgradeCardPressed]}
                          onPress={() => proPkg && handlePurchasePackage(proPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          <CtaGradient />
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={styles.upgradeCardTitle}>{billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Text>
                              <Text style={styles.upgradeCardPrice}>{proPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={styles.upgradeCardCta}>Upgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {proCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < proCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}
                  {/* Essentials downgrade (monthly-only — hidden when annual toggle active) */}
                  {billingPeriod !== 'annual' && (basicMonthly || basicAnnual) && (() => {
                    const essentialsPkg = basicMonthly ?? basicAnnual;
                    const essentialsCfg = getTierConfig('basic');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>CC Essentials — {essentialsPkg?.product?.priceString ?? '—'}/month</Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, styles.downgradeCard, { flexDirection: 'column' }, pressed && styles.downgradeCardPressed]}
                          onPress={() => openSubscriptionSettings()}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={[styles.upgradeCardTitle, styles.downgradeCardText]}>Monthly</Text>
                              <Text style={[styles.upgradeCardPrice, styles.downgradeCardText]}>{essentialsPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={[styles.upgradeCardCta, styles.downgradeCardText]}>Downgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {essentialsCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < essentialsCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}
                </>
              )}

              {/* ── FROM ESSENTIALS: dropdown with billing toggle + Chat+ primary, Pro nested ── */}
              {!freeMonthActive && currentTier === 'basic' && showAllPlans && !loading && (
                <>
                  {/* Billing period toggle */}
                  <View style={styles.billingToggleRow}>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'monthly' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('monthly')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'monthly' && styles.billingToggleTextActive]}>Monthly</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.billingToggleSegment, billingPeriod === 'annual' && styles.billingToggleSegmentActive]}
                      onPress={() => setBillingPeriod('annual')}
                    >
                      <Text style={[styles.billingToggleText, billingPeriod === 'annual' && styles.billingToggleTextActive]}>Annual</Text>
                    </Pressable>
                  </View>

                  {/* Chat+ — primary upgrade */}
                  {(fullMonthly || fullAnnual) && (() => {
                    const chatPlusPkg = billingPeriod === 'annual' ? (fullAnnual ?? fullMonthly) : (fullMonthly ?? fullAnnual);
                    const chatPlusCfg = getTierConfig('full');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>
                          CC Chat+ — {chatPlusPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}
                        </Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, { flexDirection: 'column' }, pressed && styles.upgradeCardPressed]}
                          onPress={() => chatPlusPkg && handlePurchasePackage(chatPlusPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          <CtaGradient />
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={styles.upgradeCardTitle}>{billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Text>
                              <Text style={styles.upgradeCardPrice}>{chatPlusPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={styles.upgradeCardCta}>Upgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {chatPlusCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < chatPlusCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}

                  {/* Pro — shown alongside Chat+ */}
                  {(extendedMonthly || extendedAnnual) && (() => {
                    const proPkg = billingPeriod === 'annual' ? (extendedAnnual ?? extendedMonthly) : (extendedMonthly ?? extendedAnnual);
                    const proCfg = getTierConfig('extended');
                    return (
                      <View style={styles.upgradePlanBlock}>
                        <Text style={styles.upgradePlanLabel}>
                          CC Pro — {proPkg?.product?.priceString ?? '—'}/{billingPeriod === 'annual' ? 'year' : 'month'}
                        </Text>
                        <Pressable
                          style={({ pressed }) => [styles.upgradeCard, { flexDirection: 'column' }, pressed && styles.upgradeCardPressed]}
                          onPress={() => proPkg && handlePurchasePackage(proPkg)}
                          disabled={!isUpgradeInteractive}
                        >
                          <CtaGradient />
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <View style={styles.upgradeCardLeft}>
                              <Text style={styles.upgradeCardTitle}>{billingPeriod === 'annual' ? 'Annual' : 'Monthly'}</Text>
                              <Text style={styles.upgradeCardPrice}>{proPkg?.product?.priceString ?? '—'}</Text>
                            </View>
                            <Text style={styles.upgradeCardCta}>Upgrade</Text>
                          </View>
                          <View style={styles.otherPlanBulletsCard}>
                            {proCfg.differentiators.map((item, i) => (
                              <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: i < proCfg.differentiators.length - 1 ? 4 : 0 }}>
                                <Text style={styles.otherPlanBullet}>{'\u2022'}</Text>
                                <Text style={styles.otherPlanBulletText}>{item}</Text>
                              </View>
                            ))}
                          </View>
                        </Pressable>
                      </View>
                    );
                  })()}
                </>
              )}

              {/* Close button removed — X in header provides the dismiss affordance */}

              <View style={styles.footerInlineRow}>
                <Pressable
                  style={({ pressed }) => [pressed && styles.footerInlineLinkPressed]}
                  onPress={handleRestore}
                  disabled={loading || purchasing}
                >
                  <Text style={styles.footerInlineLinkText}>Restore Purchases</Text>
                </Pressable>
                {!freeMonthActive && (currentTier === 'basic' || currentTier === 'full' || currentTier === 'extended') && (
                  <>
                    <Text style={styles.footerInlineDot}> · </Text>
                    <Pressable
                      style={({ pressed }) => [pressed && styles.footerInlineLinkPressed]}
                      onPress={openSubscriptionSettings}
                    >
                      <Text style={styles.footerInlineLinkText}>Cancel subscription</Text>
                    </Pressable>
                  </>
                )}
                {/* 🌞 Summer50 — invite-code entry (flag-gated) */}
                {summer50?.codes_enabled && (
                  <>
                    <Text style={styles.footerInlineDot}> · </Text>
                    <Pressable
                      style={({ pressed }) => [pressed && styles.footerInlineLinkPressed]}
                      onPress={handleOpenRedeem}
                      accessibilityRole="button"
                      accessibilityLabel={S50.B3_paywall_link}
                    >
                      <Text style={styles.footerInlineLinkText}>{S50.B3_paywall_link}</Text>
                    </Pressable>
                  </>
                )}
              </View>

              {/* 🕖 Seven Free Days: under the 7-day clock nobody moves to Essentials
                  on their own and there is nothing to cancel before the week ends —
                  the clock pauses Cedar. Only the store-billing sentence stays true.
                  The same for a lapsed payer (their tier reads 'trial' again after
                  EXPIRATION): they have no trial to cancel (follow-up to #3967). */}
              <Text style={styles.finePrint}>
                {!paywallStatusKnown
                  ? 'Payment is charged to your App Store or Google Play account. Manage or cancel in your device\'s subscription settings.'
                  : freeMonthActive
                  ? S50.FA_fine(faEndDate)
                  : currentTier === 'trial' && !timeBoundTrial && !lapsedPaid
                  ? trialFinePrint(storePrices.basicMonthly)
                  : 'Payment is charged to your App Store or Google Play account. Manage or cancel in your device\'s subscription settings.'}
              </Text>
            </ScrollView>
          </>
        ) : (
          <>
            {/* ── Trial header (+ optional close) ───────────────────────── */}

            <View style={styles.headerGreenWrapper}>
              <View style={styles.headerRow}>
                <View style={styles.header}>
                  <Text style={styles.title}>
                    Start your 7-day free trial
                  </Text>
                  <Text style={styles.headerPrice}>
                    {loading ? '…' : `${priceString}/month after trial.`}
                  </Text>
                  <Text style={styles.subtitle}>
                    Your coach is ready.
                  </Text>
                </View>
              </View>
            </View>

            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              {/* ── Contextual intro text ─────────────────────────────────── */}
              <Text style={styles.trialIntro}>
                You've shared a hint of where you're at and shaped how Coach Cedar coaches you. Now let's build your first plan together.
              </Text>

              {/* ── What's included — one frosted card, dark cedar ink ── */}

              <View style={styles.featuresSection}>
            <View style={styles.featuresCard}>
              <Text style={styles.cardTitleLight}>What's included</Text>
              <Text style={styles.featureCardIntroLight}>
                Your 7-day trial gives you the full Coach Cedar experience. After your trial, your CC Essentials coaching continues at {priceString}/month {'\u2014'} everything below stays with you.
              </Text>
              {FEATURES.map((feature, index) => (
                <View key={index} style={styles.featureRow}>
                  <Text style={styles.featureCardIconLight}>{feature.icon}</Text>
                  <Text style={styles.featureCardTextLight}>
                    <Text style={styles.featureCardHeadingLight}>{feature.heading}</Text>
                    {' \u2014 '}{feature.description}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* ── How your free trial works — One card, cedar subtle; title inside card ─── */}

          <View style={styles.timelineSection}>
            <View style={styles.timelineCardSingle}>
              <Text style={styles.cardTitleDark}>How your free trial works</Text>
              <View style={styles.timelineStep}>
                <View style={styles.timelineCardHeader}>
                  <View style={[styles.timelineDot, styles.timelineDotActive]} />
                  <Text style={styles.timelineDay}>Today</Text>
                </View>
                <Text style={styles.timelineDescription}>
                  Coach Cedar is ready. Set your goals, build your first plan, and see what having Coach Cedar is like.
                </Text>
              </View>
              <View style={styles.timelineStep}>
                <View style={styles.timelineCardHeader}>
                  <View style={[styles.timelineDot, styles.timelineDotPending]} />
                  <Text style={styles.timelineDay}>Days 2{'\u2013'}7</Text>
                </View>
                <Text style={styles.timelineDescription}>
                  Complete workouts and debrief with Coach Cedar. Make changes to your goals, plan, routines, and workouts anytime. Connect each morning, and on Sundays review your Coach Cedar Report and have your check-in.
                </Text>
                <Text style={[styles.timelineDescription, { marginTop: 8 }]}>
                  You can cancel anytime in your device's subscription settings. Any data generated remains in your control.
                </Text>
              </View>
              <View style={[styles.timelineStep, styles.timelineStepLast]}>
                <View style={styles.timelineCardHeader}>
                  <View style={[styles.timelineDot, styles.timelineDotPending]} />
                  <Text style={styles.timelineDay}>After your trial</Text>
                </View>
                <Text style={styles.timelineDescription}>
                  Your CC Essentials subscription begins at{' '}
                  <Text style={styles.timelineDescriptionBold}>{priceString}/month</Text>
                  {' '}{'\u2014'} everything above is included {'\u2014'} unless you've cancelled. Cancel anytime. Upgrade to CC Chat+ or CC Pro for premium features.
                </Text>
              </View>
              {freeMonthDeferred && (
                <View style={faStyles.deferredRibbon}>
                  <Text style={faStyles.deferredText}>{S50.FA_deferred_ribbon(faPill, faStartDate)}</Text>
                </View>
              )}
            </View>
          </View>

          {/* ── CTA + Price — Inline row ──────────────────────────── */}

          {Platform.OS === 'web' ? (
            /* Web: RevenueCat unavailable — offer mobile download or proceed */
            <View style={{ paddingBottom: 8 }}>
              <Text style={styles.loadingPlansText}>
                Subscriptions are available in the Coach Cedar app. Download on the App Store or Google Play to start your free 7-day trial.
              </Text>
              <View style={styles.storeLinksRow}>
                <Pressable
                  style={({ pressed }) => [styles.storeLinkButton, pressed && styles.storeLinkButtonPressed]}
                  onPress={() => Linking.openURL('https://apps.apple.com/app/coach-cedar/id6758776684')}
                >
                  <Text style={styles.storeLinkText}>App Store</Text>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [styles.storeLinkButton, pressed && styles.storeLinkButtonPressed]}
                  onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.coachcedar.app')}
                >
                  <Text style={styles.storeLinkText}>Google Play</Text>
                </Pressable>
              </View>
              <Pressable
                style={({ pressed }) => [styles.ctaButton, pressed && styles.ctaButtonPressed]}
                onPress={() => onClose()}
              >
                <CtaGradient themed />
                <Text style={styles.ctaButtonText}>Continue using Coach Cedar on the web</Text>
              </Pressable>
            </View>
          ) : (
            /* Mobile: standard RevenueCat trial flow */
            <>
              {!loading && !trialPackage && loadError && (
                <View style={styles.emptyPlansBlock}>
                  <Text style={styles.loadErrorText}>{loadError}</Text>
                  <AnimatedPressable
                    variant="standard"
                    style={({ pressed }) => [styles.retryButton, pressed && styles.retryButtonPressed]}
                    onPress={() => {
                      setLoadError(null);
                      setLoading(true);
                      isLoadingPackagesRef.current = false;
                      loadPackages();
                    }}
                  >
                    <Text style={styles.retryButtonText}>Try Again</Text>
                  </AnimatedPressable>
                </View>
              )}

              {(!loadError || trialPackage) && (
                <View style={styles.ctaPriceBlock}>
                  <Text style={styles.ctaPriceAmount}>
                    {loading ? '…' : priceString}/month
                  </Text>
                  <Text style={styles.ctaPriceSub}>
                    after your 7-day free trial
                  </Text>
                  <Text style={styles.ctaChargeDate}>
                    {`You won't be charged until ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Cancel anytime before your trial ends.`}
                  </Text>
                </View>
              )}
              <View style={styles.ctaRow}>
                <AnimatedPressable
                  variant="cta"
                  themed
                  style={({ pressed }) => [
                    styles.ctaButton,
                    !isInteractive && styles.ctaButtonDisabled,
                    pressed && isInteractive && styles.ctaButtonPressed,
                  ]}
                  onPress={handleStartTrial}
                  disabled={!isInteractive}
                >
                  {purchasing ? (
                    <ActivityIndicator color={palette.ink.onCta} />
                  ) : (
                    <Text style={styles.ctaButtonText}>Start free trial</Text>
                  )}
                </AnimatedPressable>
              </View>
            </>
          )}

          {/* ── Restore purchases ──────────────────────────────────── */}

          <AnimatedPressable
            variant="standard"
            style={({ pressed }) => [
              styles.restoreButton,
              pressed && !loading && styles.restoreButtonPressed,
            ]}
            onPress={handleRestore}
            disabled={loading || purchasing}
          >
            <Text style={styles.restoreButtonText}>Restore Purchases</Text>
          </AnimatedPressable>

          {/* ── Fine print ─────────────────────────────────────────── */}

          {Platform.OS === 'android' ? (
            <Text style={styles.finePrint}>
              Payment is charged to your Google Play account. Your subscription auto-renews at {priceString}/month after the free trial unless canceled before the trial ends. Manage or cancel in Google Play {'>'} Subscriptions. By tapping "Start your free 7-day trial," you agree to our{' '}
              <Text
                style={styles.finePrintLink}
                onPress={() => Linking.openURL('https://coachcedar.com/terms')}
              >
                Terms of Use
              </Text>
              {' '}and{' '}
              <Text
                style={styles.finePrintLink}
                onPress={() => Linking.openURL('https://coachcedar.com/privacy')}
              >
                Privacy Policy
              </Text>
              .
            </Text>
          ) : (
            <Text style={styles.finePrint}>
              Payment is charged to your Apple ID. Your subscription auto-renews at {priceString}/month after the free trial unless canceled at least 24 hours before the end of the trial period. Manage or cancel in Settings {'>'} Subscriptions. By tapping "Start your free 7-day trial," you agree to our{' '}
              <Text
                style={styles.finePrintLink}
                onPress={() => Linking.openURL('https://coachcedar.com/terms')}
              >
                Terms of Use
              </Text>
              {' '}and{' '}
              <Text
                style={styles.finePrintLink}
                onPress={() => Linking.openURL('https://coachcedar.com/privacy')}
              >
                Privacy Policy
              </Text>
              .
            </Text>
          )}

          {/* ── No thanks (trial mode only) ─────────────────────────── */}

          {mode === 'trial' && onDecline && (
            <AnimatedPressable
              variant="standard"
              style={({ pressed }) => [
                styles.noThanksButton,
                pressed && styles.noThanksButtonPressed,
              ]}
              onPress={() => {
                onDecline();
                onClose();
              }}
              disabled={purchasing}
              accessibilityRole="button"
              accessibilityLabel="No, Thank you"
            >
              <Text style={styles.noThanksButtonText}>No, Thank you</Text>
            </AnimatedPressable>
          )}
        </ScrollView>

        {/* ── Loading overlay (first load only) ───────────────────── */}

        {loading && !trialPackage && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={palette.ink.accent} />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        )}
          </>
        )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// 🌞 Free Access presentation styles (mock 2026-07-16)
const useFaStyles = makeStyles((t) => ({
  pill: {
    backgroundColor: themed(t, '#0E4632', t.fill.brand),
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 12,
  },
  pillText: { color: t.ink.onFill, fontSize: 11, fontWeight: '800', letterSpacing: 0.8 },
  line: { fontSize: 13, color: t.ink.onFill, opacity: 0.95, marginTop: 6, width: '100%' },
  meterBar: { height: 6, borderRadius: 999, backgroundColor: onGlass(0.28), overflow: 'hidden', width: '100%' },
  meterFill: { height: '100%', borderRadius: 999, backgroundColor: t.ink.onFill },
  meterCap: { fontSize: 11, color: t.ink.onFill, opacity: 0.9 },
  deferredRibbon: {
    marginTop: 10,
    backgroundColor: themed(t, 'rgba(227,167,46,0.18)', t.status.warningTint),
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 12,
  },
  deferredText: { fontSize: 12, fontWeight: '700', color: themed(t, '#FFF3DC', t.status.warningStrong), letterSpacing: 0.3 },
  // FA plans list — neutral, non-pressable option cards
  // Frost — they were plain white slabs beside a saturated hero.
  planCard: {
    ...t.material.cedarFrost,
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  planHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 },
  planName: { fontSize: 16.5, fontWeight: '800', letterSpacing: -0.2, color: themed(t, '#1A2B22', t.ink.primary) },
  planPrice: { fontSize: 14.5, fontWeight: '700', color: t.ink.accent },
  planPer: { fontSize: 11.5, fontWeight: '600', color: themed(t, '#8A978F', t.ink.tertiary) },
  planDesc: { fontSize: 12.8, color: themed(t, '#5B6B62', t.ink.secondary), marginTop: 4, lineHeight: 18 },
  planLevelTag: {
    alignSelf: 'flex-start',
    marginTop: 8,
    borderWidth: 1.5,
    borderColor: t.ink.accent,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 9,
  },
  planLevelTagText: { fontSize: 10.5, fontWeight: '800', letterSpacing: 0.8, color: t.ink.accent },
  planFoot: {
    marginTop: 4,
    marginHorizontal: 6,
    fontSize: 12.3,
    color: themed(t, '#8A978F', t.ink.tertiary),
    lineHeight: 18,
    textAlign: 'center',
  },
}));

const useStyles = makeStyles((t) => ({
  // Transparent: the ground is <CanvasGradient />, mounted as the first child.
  // A gradient is not a colour, so it cannot live in `backgroundColor` — and a
  // flat white here would paint OVER it. This surface stood on plain #FFFFFF
  // while every tab behind it had already moved to the Daylight canvas.
  container: {
    flex: 1,
  },
  // Partner codes C5: the neutral wait while the partner offer gate decides.
  partnerChecking: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollContent: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.sm,
    paddingBottom: Spacing['4xl'],
  },

  // Header row (title + optional close button) ───────────────────────

  headerGreenWrapper: {
    // Daylight S5 scope fix (founder on-device 2026-07-29): this sheet lives in
    // components/ and is a <Modal>, not a BottomSheet, so the Roots sweep missed
    // it — it was the only sheet still wearing a cedarPrime band.
    backgroundColor: t.glass.roots,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: Spacing.xl,
    marginBottom: Spacing.xs,
  },
  headerBanner: {
    paddingTop: Spacing.md,
    paddingBottom: Spacing.xl,
    paddingLeft: Spacing.lg,
    paddingRight: 48,
  },
  headerCloseButton: {
    position: 'absolute' as const,
    top: Spacing.lg,
    right: Spacing.md,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    zIndex: 10,
  },
  headerCloseButtonText: {
    fontSize: 32,
    color: onGlass(0.9),
    lineHeight: 32,
    textShadowColor: withAlpha(t.shadow.deep, 0.4),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 6,
  },
  headerBannerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: t.ink.onFill,
    marginBottom: 2,
  },
  headerBannerSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    color: onGlass(0.85),
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: Spacing.lg,
    paddingBottom: Spacing.sm,
    paddingHorizontal: Spacing.xl,
  },
  header: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: t.ink.onFill,
    textAlign: 'center',
    marginBottom: 4,
    lineHeight: 30,
    flexWrap: 'wrap',
  },
  headerPrice: {
    fontSize: 18,
    fontWeight: '700',
    color: t.ink.onFill,
    textAlign: 'center',
    marginBottom: Spacing.sm,
    lineHeight: 24,
  },
  subtitle: {
    fontSize: 15,
    color: onGlass(0.8),
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: Spacing.sm,
  },
  trialIntro: {
    fontSize: 14,
    color: t.ink.secondary,
    lineHeight: 21,
    marginBottom: Spacing.lg,
  },

  // What's included — one FROSTED card with dark cedar ink ────────────────
  //
  // Was cedarLight with white text across five keys: 3.00:1, an AA failure,
  // and the saturated fill is what made this section read heavy and washed at
  // once. This is a CONTAINER you read, not a button you press, so it takes
  // the quiet treatment rather than the ramp (see CEDAR_FROST).

  featuresSection: {
    marginBottom: Spacing['2xl'],
  },
  featuresCard: {
    ...t.material.cedarFrost,
    borderRadius: 14, // overrides the token's 12 — this card's own radius
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.lg,
  },
  cardTitleLight: {
    fontSize: 13,
    fontWeight: '700',
    color: t.ink.onCedarFrost,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: Spacing.lg,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.md,
    marginBottom: Spacing.md,
  },
  featureCardIconLight: {
    fontSize: 18,
    color: t.ink.onCedarFrost,
    marginTop: 2,
  },
  featureCardTextLight: {
    fontSize: 15,
    color: t.ink.onCedarFrost,
    flex: 1,
    lineHeight: 22,
  },
  featureCardHeadingLight: {
    fontWeight: '700',
  },
  featureCardIntroLight: {
    fontSize: 15,
    color: t.ink.onCedarFrost,
    lineHeight: 22,
    // No `opacity` here: on white ink it was a legibility tax, and on frost ink
    // it would undo the 10.3:1 this card was converted to earn. The intro reads
    // as secondary through size and weight, not by being faded.
    marginBottom: 12,
  },

  // How it works — One card, cedar subtle; title inside card ───────────────

  timelineSection: {
    marginBottom: Spacing['2xl'],
  },
  timelineCardSingle: {
    backgroundColor: t.surface.subtle,
    borderRadius: 14,
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.lg,
  },
  cardTitleDark: {
    fontSize: 13,
    fontWeight: '700',
    color: t.ink.strong,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: Spacing.lg,
  },
  timelineStep: {
    marginBottom: Spacing.lg,
  },
  timelineStepLast: {
    marginBottom: 0,
  },
  timelineCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    marginBottom: 6,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  timelineDotActive: {
    backgroundColor: t.ink.accent,
  },
  timelineDotPending: {
    backgroundColor: t.surface.subtle,
    borderWidth: 2,
    borderColor: t.ink.accent,
  },
  timelineDay: {
    fontSize: 15,
    fontWeight: '700',
    color: t.ink.strong,
  },
  timelineDescription: {
    fontSize: 14,
    color: t.ink.primary,
    lineHeight: 20,
    paddingLeft: 24,
  },
  timelineDescriptionBold: {
    fontWeight: '700',
    color: t.ink.primary,
  },

  // CTA ───────────────────────────────────────────────────────────────────

  ctaPriceBlock: {
    alignItems: 'center',
    marginBottom: Spacing.sm,
    paddingHorizontal: Spacing.sm,
  },
  ctaPriceAmount: {
    fontSize: 22,
    fontWeight: '700',
    color: t.ink.accent,
    textAlign: 'center',
  },
  ctaPriceSub: {
    fontSize: 13,
    color: t.ink.secondary,
    textAlign: 'center',
    marginTop: 2,
  },
  ctaChargeDate: {
    fontSize: 12,
    color: t.ink.secondary,
    textAlign: 'center',
    marginTop: 6,
    lineHeight: 17,
    paddingHorizontal: Spacing.sm,
  },
  ctaRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: Spacing.md,
  },
  ctaButton: {
    backgroundColor: t.cta.fill, // fallback under the gradient
    borderRadius: 8,
    overflow: 'hidden', // clips CtaGradient's 24pt over-bleed
    borderWidth: 1,
    borderColor: t.cta.edge,
    paddingVertical: 14,
    paddingHorizontal: Spacing.lg,
    alignItems: 'center',
  },
  ctaButtonDisabled: {
    opacity: 0.55,
  },
  // A pressed FILL is invisible under the opaque CtaGradient — gradient
  // surfaces press by dimming (also retires white-on-thrive at 1.90:1).
  ctaButtonPressed: { ...t.glass.pressed },
  ctaButtonText: {
    color: t.ink.onCta,
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.3,
  },

  // Restore ───────────────────────────────────────────────────────────────

  restoreButton: {
    padding: Spacing.md,
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  restoreButtonText: {
    color: t.ink.accent,
    fontSize: 15,
    fontWeight: '600',
  },
  restoreButtonPressed: {
    opacity: 0.6,
  },

  // Footer inline row (Restore · Cancel on one line) ─────────────────────
  footerInlineRow: {
    flexDirection: 'row' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingVertical: Spacing.sm,
    marginBottom: Spacing.sm,
  },
  footerInlineLinkText: {
    fontSize: 13,
    color: t.ink.tertiary,
  },
  footerInlineLinkPressed: {
    opacity: 0.5,
  },
  footerInlineDot: {
    fontSize: 13,
    color: t.ink.tertiary,
  },

  // Upgrade mode — loading/empty ────────────────────────────────────────

  loadingPlansText: {
    fontSize: 15,
    color: t.ink.tertiary,
    textAlign: 'center',
    paddingVertical: Spacing.xl,
  },

  loadErrorText: {
    fontSize: 14,
    color: themed(t, '#D9534F', t.status.errorStrong),
    textAlign: 'center',
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.sm,
    paddingBottom: Spacing.xs,
  },
  emptyPlansBlock: {
    alignItems: 'center',
    paddingVertical: Spacing.xl,
    paddingHorizontal: Spacing.lg,
  },
  retryButton: {
    marginTop: Spacing.md,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.xl,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: t.ink.accent,
  },
  retryButtonPressed: {
    opacity: 0.7,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: t.ink.accent,
    textAlign: 'center' as const,
  },
  storeLinksRow: {
    flexDirection: 'row',
    gap: Spacing.md,
    marginTop: Spacing.lg,
  },
  storeLinkButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: t.fill.brand,
  },
  storeLinkButtonPressed: {
    opacity: 0.85,
  },
  storeLinkText: {
    fontSize: 15,
    fontWeight: '600',
    color: t.ink.onFill,
  },

  // Fine print ────────────────────────────────────────────────────────────

  finePrint: {
    fontSize: 11,
    color: t.ink.tertiary,
    textAlign: 'center',
    lineHeight: 16,
    paddingHorizontal: Spacing.md,
  },
  finePrintLink: {
    fontSize: 11,
    color: t.ink.accent,
    textDecorationLine: 'underline',
  },

  // No thanks button ──────────────────────────────────────────────────────
  noThanksButton: {
    marginTop: Spacing.lg,
    marginBottom: Spacing.md,
    paddingVertical: Spacing.sm,
    alignSelf: 'center',
  },
  noThanksButtonPressed: {
    opacity: 0.5,
  },
  noThanksButtonText: {
    fontSize: 14,
    color: t.ink.tertiary,
    textDecorationLine: 'underline',
    textAlign: 'center',
  },

  // Upgrade mode — plan blocks ─────────────────────────────────────────

  upgradePlanBlock: {
    marginBottom: Spacing.xl,
  },
  upgradePlanLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: t.ink.primary,
    marginBottom: 3,
  },
  trialPlanBulletsCard: {
    marginBottom: Spacing.sm,
    paddingHorizontal: 4,
  },
  trialPlanBullet: {
    fontSize: 15,
    color: t.ink.secondary,
    lineHeight: 21,
    marginRight: 6,
  },
  trialPlanBulletText: {
    fontSize: 15,
    color: t.ink.secondary,
    lineHeight: 21,
    flex: 1,
  },
  otherPlanBulletsCard: {
    marginTop: 10,
    ...t.material.dataPanel,
    borderRadius: 8,
    padding: 12,
    width: '100%' as const,
  },
  otherPlanBullet: {
    fontSize: 13,
    color: themed(t, '#888', t.ink.secondary),
    lineHeight: 19,
    marginRight: 6,
  },
  otherPlanBulletText: {
    fontSize: 13,
    color: themed(t, '#666', t.ink.primary),
    lineHeight: 19,
    flex: 1,
  },
  // Hero card, ramped (founder 2026-08-05: "make them hero cards (ramp)").
  //
  // This was the worst contrast number in the codebase: `cedarLight` under a
  // View-level `opacity: 0.7` with white ink composited to ~2.08:1 at rest,
  // and the pressed fill took it to ~1.59:1 — on the purchase surface. The
  // View opacity is the part that made it unreasonable-about: it multiplies
  // the LABEL as well as the fill, so no ink choice could rescue it.
  //
  // No `backgroundColor` here on purpose. The upgrade rows mount <CtaGradient />
  // (every stop clears AA for white: 11.67:1 → 4.56:1) while `downgradeCard`
  // supplies its own light fill — one base style, two materials, and the ramp
  // must never mount on the light one (its ink is cedarPrime).
  upgradeCard: {
    borderRadius: 12,
    overflow: 'hidden', // clips the ramp's 24pt over-bleed
    paddingVertical: 12,
    paddingHorizontal: Spacing.lg,
    ...t.glass.edge, // the lit edge that reads as thickness, not paint
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  upgradeCardLeft: {
    flex: 1,
  },
  upgradeCardDisabled: {
    opacity: 0.6,
  },
  // A pressed FILL is invisible under the opaque ramp — the gradient family
  // presses by lighting its edge. Same shape as `ctaButtonPressed` above.
  upgradeCardPressed: { ...t.glass.pressed },
  upgradeCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: t.ink.onFill,
    marginBottom: Spacing.xs,
  },
  upgradeCardPrice: {
    fontSize: 14,
    color: t.ink.onFill,
  },
  upgradeCardCta: {
    fontSize: 14,
    fontWeight: '600',
    color: t.ink.onFill,
    marginLeft: Spacing.md,
  },

  // Current plan section ─────────────────────────────────────────────────

  currentPlanBlock: {
    marginBottom: Spacing.lg,
  },
  currentPlanSectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ink.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: Spacing.sm,
  },
            // Ramp, not cedarLight (founder 2026-08-04: "subscription drawer
            // cards are not updated"). This is the hero STATE card — the plan
            // you are on — and its white ink measured 3.00:1 on cedarLight,
            // the same AA failure this work retired elsewhere. White holds the
            // whole ramp: 11.67:1 at the top, 4.56:1 at the bottom.
            currentPlanCard: {
              flexDirection: 'column',
              ...t.glass.edge,
              overflow: 'hidden',
              borderRadius: 12,
              padding: Spacing.lg,
              paddingVertical: Spacing.xl,
              shadowColor: themed(t, t.fill.brandDark, t.shadow.ink),
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.08,
              shadowRadius: 6,
              marginBottom: Spacing.md,
              elevation: 2,
            },
  downgradeCard: {
    backgroundColor: t.surface.subtle,
    borderColor: 'transparent',
  },
            downgradeCardText: {
              color: t.ink.accent,
            },
  downgradeCardPressed: {
    backgroundColor: t.surface.well,
    borderColor: t.surface.well,
  },
  // Billing period pill toggle
  billingToggleRow: {
    flexDirection: 'row' as const,
    alignSelf: 'center' as const,
    backgroundColor: t.surface.subtle,
    borderRadius: 10,
    marginBottom: Spacing.lg,
    padding: 3,
  },
  billingToggleSegment: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  billingToggleSegmentActive: {
    backgroundColor: t.fill.brand,
  },
  billingToggleText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: t.ink.accent,
  },
  billingToggleTextActive: {
    color: t.ink.onFill,
  },
  // Trial CTA button (full-width cedar green, thrive on press)
  // "Subscribe Now" / "Start free trial" — the highest-intent button in the app.
  //
  // It is `variant="cta"` with a cedarPrime fill, which means AnimatedPressable
  // paints an OPAQUE CtaGradient over it. So `trialCtaButtonPressed`'s mint FILL
  // was invisible: this button has not acknowledged a press since the ramp
  // shipped (2026-08-02). The gate that guards it stayed green the whole time,
  // because it asserted the property EXISTS, not that it can be SEEN.
  //
  // The 2026-04-25 directive — a primary CTA next to a green resting state must
  // press to mint — is unchanged. Only the mechanism moved: on a gradient the
  // mint has to live on the EDGE (CTA_GRADIENT_PRESSED / PRESSED_THRIVE).
  //
  // ⚠️ The border below is load-bearing. AnimatedPressable injects GLASS_EDGE
  // LAST whenever the caller declares none, which would overwrite the pressed
  // borderColor. Declaring it here is what makes the press expressible.
  trialCtaButton: {
    backgroundColor: t.cta.fill, // fallback under the gradient
    borderRadius: 14,
    overflow: 'hidden', // clips the ramp's 24pt over-bleed
    borderWidth: 1,
    borderColor: t.cta.edge,
    paddingVertical: 15,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    marginTop: Spacing.sm,
  },
  trialCtaButtonPressed: { ...t.glass.pressed },
  trialCtaButtonText: {
    color: t.ink.onCta,
    fontSize: 17,
    fontWeight: '700' as const,
  },
  // See all plans expandable link
  seeAllPlansButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 30,
    paddingVertical: Spacing.md,
    marginBottom: Spacing.sm,
  },
  seeAllPlansButtonPressed: {
    opacity: 0.6,
  },
  seeAllPlansText: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: t.ink.accent,
  },
  currentPlanName: {
    fontSize: 16,
    fontWeight: '700',
    color: t.ink.onFill,
    marginBottom: 2,
  },
  currentPlanPrice: {
    fontSize: 13,
    color: t.fill.neutral,
    fontWeight: '400',
  },
  currentPlanActiveBadge: {
    backgroundColor: t.fill.brand,
    borderRadius: 8,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 4,
    marginLeft: Spacing.sm,
  },
  currentPlanActiveBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ink.onFill,
  },
  otherPlansToggle: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
    paddingVertical: Spacing.md,
    marginBottom: Spacing.sm,
  },
  otherPlansTogglePressed: {
    opacity: 0.6,
  },
  otherPlansSectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ink.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },

  // Loading overlay ──────────────────────────────────────────────────────

  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: withAlpha(t.canvas.page, 0.9),
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: Spacing.md,
    color: t.ink.strong,
    fontSize: 15,
    fontWeight: '500',
  },
  // ── Trial Context Card (active trial) ──
    // Container writing WHITE on cedarLight — 2.36:1 to 3.00:1, under the
    // 4.5:1 AA floor. Found by the container scan run WITHOUT the
    // button|btn|cta filter, which is how the completion card escaped the
    // first sweep (founder 2026-08-02). cedarPrime carries white at 6.33:1
    // and keeps the green identity these cards are meant to have.
  trialContextCard: {
    backgroundColor: t.fill.brand,
    borderRadius: 14,
    padding: 20,
    marginBottom: 20,
  },
  trialContextTitle: {
    fontSize: 17,
    fontWeight: '700' as const,
    color: t.ink.onFill,
    marginBottom: 8,
  },
  trialContextBody: {
    fontSize: 15,
    color: onGlass(0.9),
    lineHeight: 22,
    marginBottom: 14,
  },
  trialContextBenefits: {
    gap: 6,
  },
  trialContextBenefitRow: {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    gap: 8,
  },
  trialContextCheck: {
    color: t.ink.onFill,
    fontSize: 15,
    fontWeight: '600' as const,
    lineHeight: 20,
  },
  trialContextBenefitText: {
    fontSize: 14,
    color: onGlass(0.9),
    lineHeight: 20,
    flex: 1,
  },
  // ── Expired Trial Context Card ──
  expiredContextCard: {
    backgroundColor: t.surface.subtle,
    borderRadius: 14,
    padding: 20,
    marginBottom: 20,
  },
  expiredContextTitle: {
    fontSize: 17,
    fontWeight: '700' as const,
    color: t.ink.primary,
    marginBottom: 8,
  },
  expiredContextBody: {
    fontSize: 15,
    color: t.ink.primary,
    lineHeight: 22,
  },
}));
